import React, { useEffect, useRef } from "react";
import useKeyUp from "utils/effects/useKeyUp";
import Portal from "components/atom/Portal";
import Icon from "components/atom/Icon";
import { useTranslation } from "react-i18next";

type PropsType = {
  flexibleHeight?: boolean
  swipeable?: boolean
  onClose: () => void
  onSwipeUp?: () => void
  onSwipeDown?: () => void
  onSwipeClick?: () => void
  full?: boolean
  children: React.ReactNode
  className?: string
  containerClassName?: string
  noOverflow?: boolean
  canClose?: boolean
  large?: boolean
  noPadding?: boolean
  canGoBack?: boolean
  onGoBack?: () => void
  position?: "center" | "top" | "bottom"
  btnRightChildren?: React.ReactNode
}

function Modal(props: PropsType) {
  const {
    flexibleHeight = false,
    position = "center",
    swipeable = false,
    onClose,
    onGoBack = () => {
    },
    full = false,
    children = null,
    className = "",
    containerClassName = "",
    noOverflow = false,
    canClose = true,
    large = false,
    noPadding = false,
    canGoBack = false,
    btnRightChildren = null
  } = props;

  const { t } = useTranslation();
  const startY = useRef<number>(0);


  useEffect(() => {
    document.body.classList.add(`modal-open`);
    return () => {
      document.body.classList.remove(`modal-open`);
    };
  }, []);

  const handleTouchStart = (e: any) => {
    if (!props.onSwipeUp && !props.onSwipeDown) {
      return;
    }
    if (!swipeable) {
      return;
    }
    startY.current = e.touches[0].clientY;
  };
  const handleSwipeClick = () => {
    if (props.onSwipeClick){
      props.onSwipeClick();
    }
  }

  const handleTouchMove = (e: any) => {
    if (!props.onSwipeUp && !props.onSwipeDown) {
      return;
    }
    if (!swipeable) {
      return;
    }
    const currentY = e.touches[0].clientY;
    const diff = startY.current - currentY;

    if (diff > 60) {
      if (props.onSwipeUp) {
        props.onSwipeUp();
      }
    } else if (diff < -60) {
      if (props.onSwipeDown) {
        props.onSwipeDown();
      }
    }
  };


  useKeyUp("Escape", () => {
    if (!canClose) return;
    onClose();
  });


  return (
    <Portal>
      <div
        className={`modal ${swipeable ? "-swipeable" : ""} -${position} ${noPadding ? "" : "-padding"} ${large ? "-large" : ""} ${full ? "-full" : ""} ${flexibleHeight ? "-flexible-h" : ""} ${className} ${noOverflow ? "-no-overflow" : ""}`}>
        <div className="modal__overlay" onClick={onClose} />

        <div className={`modal__container ${containerClassName}`}>
          <div className="modal__header">
            {canGoBack ? (
              <div className="w-1/3 flex flex-row justify-between items-center gap-2 cursor-pointer pointer-events-auto"
                   onClick={() => onGoBack()}>
                <div className="modal__header__btn">
                  <Icon className="h-5 min-w-5 w-5 text-black" name="angle-left" />
                </div>
                <p className="text-black">
                  {t("goBack")}
                </p>
              </div>
            ) : (
              <div className="w-1/3" />
            )}

            {swipeable && (
              <div
                onClick={handleSwipeClick}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                className="w-1/3 flex flex-row justify-center items-center pointer-events-auto">
                <div>
                  <Icon className="h-5 min-w-5 w-5 text-black" name="bars-2" />
                </div>
              </div>
            )}

            {canClose && (
              <div className="w-1/3 flex flex-row justify-end items-center">
                <div className="flex flex-row justify-between items-center gap-2">
                  {btnRightChildren}
                  <div className="modal__header__btn" onClick={onClose}>
                    <Icon className="h-5 min-w-5 w-5 text-black" name="x" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal__content">
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default Modal;
